import { CSSProperties, ReactNode, useRef } from 'react';

import { cx } from '@libs/classnames';
import { useForceUpdate } from '@libs/hooks/useForceUpdate';

import styles from './AnimationCrossfade.module.css';

const DEFAULT_TIME_MS = 500;

export function AnimationCrossfade({
  children,
  contentKey,
  timeMs = DEFAULT_TIME_MS,
  inline,
  variant = 'one-then-another',
}: {
  children: ReactNode;
  contentKey: string | number;
  inline?: boolean;
  timeMs?: number;
  variant?:
    | 'one-then-another' // Сначала один элемент прячется, потом появляется второй
    | 'cross-fade'; // Элементы синхронно появляются и исчезают, растворяя друг друга
}) {
  const prevRef = useRef({
    children,
    contentKey,
  });
  const animationRef = useRef({
    from: children,
    fromKey: undefined as string | number | undefined,
  });
  const prev = prevRef.current;
  const changed = prev.contentKey !== contentKey;
  prevRef.current = { contentKey, children };

  if (changed) {
    animationRef.current = {
      from: prev.children,
      fromKey: prev.contentKey,
    };
  }

  const forceUpdate = useForceUpdate();
  const onAnimationStop = () => {
    animationRef.current = {
      from: children,
      fromKey: undefined,
    };
    forceUpdate();
  };

  const animationState = animationRef.current;
  const isAnimating = animationState.fromKey !== undefined;

  return (
    <div
      className={cx(
        styles.crossfade,
        {
          [styles.is_inline]: inline,
          [styles.is_animating]: isAnimating,
        },
        styles[variant],
      )}
      style={{ '--time': `${timeMs}ms` } as CSSProperties}
    >
      {isAnimating && (
        <div key={animationState.fromKey} className={styles.from}>
          {animationState.from}
        </div>
      )}
      <div className={styles.to} key={contentKey} onAnimationEnd={onAnimationStop}>
        {children}
      </div>
    </div>
  );
}
